import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { IMAGES } from "@/lib/data";
import PageHead from "@/components/site/PageHead";
import { Reveal, Eyebrow } from "@/components/site/Motion";

const LOOP = [
  { num: "01", title: "Agree", short: "Common crosswalk and signed MOU.", detail: "Every participating agency signs onto a common crosswalk and MOU before any training begins — the shared definition of what \u201cready\u201d means, by role." },
  { num: "02", title: "Train", short: "Standardized skills, observable criteria.", detail: "Standardized skills are taught against observable, scored criteria — not general awareness content, but rehearsable behavior." },
  { num: "03", title: "Exercise", short: "Shared zoning, command, handoff.", detail: "Agencies rehearse shared zoning, command, and handoff together, under conditions an outside evaluator can observe and score." },
  { num: "04", title: "Measure", short: "Time, accuracy, adherence, data quality.", detail: "Time, accuracy, protocol adherence, and data quality are captured against a predefined measure dictionary." },
  { num: "05", title: "Correct", short: "Assign, close, and retest findings.", detail: "After-action findings are assigned an owner, closed, and retested. Nothing is left as an unassigned recommendation." },
  { num: "06", title: "Replicate", short: "Only if evidence supports it.", detail: "Expansion happens only if the independent evaluation supports it. A completed pilot is not, by itself, a reason to scale." },
];

const TABS = [
  { id: "c01", num: "01", title: "Common Readiness Crosswalk", purpose: "Define what \u201cready\u201d means, by role, across every participating agency.", role: "Medical director and each agency lead jointly approve the crosswalk.", deliverables: "A competency map aligning TECC/TCCC, national EMS education standards, NIMS, local protocols, and hospital handoff expectations.", evidence: "A version-controlled crosswalk approved by the medical director and each agency lead." },
  { id: "c02", num: "02", title: "Joint Scenario Curriculum", purpose: "Teach and rehearse the crosswalk through role-specific and multi-agency simulation.", role: "Academies and simulation centers deliver; agencies release staff time.", deliverables: "Role-specific classroom modules, skills stations, and multi-agency simulation packages.", evidence: "Published objectives, instructor guides, attendance records, fidelity checklists, scored exercises." },
  { id: "c03", num: "03", title: "Readiness Measurement", purpose: "Count what training can actually change, using defined and auditable indicators.", role: "Independent evaluator defines and audits the measure set.", deliverables: "A small set of defined training and exercise measures, NEMSIS-compatible where applicable.", evidence: "Data dictionary, inter-rater reliability checks, data-quality report, baseline and follow-up results." },
  { id: "c04", num: "04", title: "After-Action Learning", purpose: "Close the loop: findings are assigned, corrected, and retested — not filed away.", role: "Steering group tracks ownership and closure.", deliverables: "Structured debrief protocol, corrective-action register, and closure tracking.", evidence: "Named action owners, due dates, retest results, documented protocol revisions." },
  { id: "c05", num: "05", title: "Train-the-Trainer & QA", purpose: "Ensure quality survives beyond the founding cohort as the program scales.", role: "Academy/training lead certifies and audits instructors locally.", deliverables: "Instructor practicum, observation rubric, recertification cycle, and site audit.", evidence: "Instructor files, observed-teaching scores, drift monitoring, corrective coaching records." },
];

const CROSSWALK = [
  ["TECC / TCCC", "Evidence-based tactical care guidance for civilian and military settings.", "Operational crosswalk, local implementation, joint exercise, and retention testing."],
  ["National EMS Education Standards / Scope Model", "Defines minimum educational objectives and recommended practice levels.", "Active-threat competency mapping within local authorization and medical direction."],
  ["NIMS / ICS", "Common incident-management language, unified command, coordination doctrine.", "Medical-readiness scenarios that test whether agencies can apply those principles under pressure."],
  ["NEMSIS", "Standardizes national EMS patient-care data and maintains a national repository.", "A limited readiness-data module or linked dataset; no replacement patient registry."],
  ["NEMSQA", "Develops and reports standardized EMS quality measures, including trauma measures.", "Exercise- and training-focused measures crosswalked to existing quality concepts."],
  ["NEMSAC 2024 advisory", "Recommends active-threat education, interoperability, MOUs, demonstration projects.", "A bounded pilot that implements and evaluates selected recommendations."],
];

export default function Framework() {
  const [active, setActive] = useState(0);
  const [tab, setTab] = useState(0);

  return (
    <main data-testid="framework-page">
      <PageHead
        chapter="03"
        eyebrow="Framework"
        titleLines={["Program", "Architecture."]}
        lede="T-EMRP is defined by four commitments: inherit the clinical content, standardize the observable behavior, measure what training can actually change, and let an independent evaluator grade the result."
        image={IMAGES.simulation}
      />

      {/* CLOSED LOOP */}
      <section className="py-24 max-w-7xl mx-auto px-6 lg:px-10" data-testid="closed-loop-section">
        <Reveal>
          <Eyebrow>The closed loop</Eyebrow>
          <h2 className="mt-5 font-display font-extrabold text-white tracking-tight leading-tight" style={{ fontSize: "clamp(1.75rem, 4vw, 3rem)" }}>
            Six stages. The loop closes on itself.
          </h2>
          <p className="mt-4 text-sm text-mist">Select a stage to see what it means in practice.</p>
        </Reveal>

        <div className="mt-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-line border border-line">
          {LOOP.map((s, i) => (
            <button
              key={s.num}
              data-testid={`loop-step-${s.num}`}
              onClick={() => setActive(i)}
              className={`text-left p-6 transition-colors duration-300 ${active === i ? "bg-signal text-white" : "bg-ink hover:bg-surface"}`}
            >
              <span className={`font-mono text-xs ${active === i ? "text-white/80" : "text-signal"}`}>{s.num}</span>
              <h3 className="mt-3 font-display font-bold text-lg tracking-tight text-white">{s.title}</h3>
              <p className={`mt-1.5 text-xs leading-relaxed ${active === i ? "text-white/80" : "text-slate-400"}`}>{s.short}</p>
            </button>
          ))}
        </div>
        <AnimatePresence mode="wait">
          <motion.div
            key={active}
            data-testid="loop-detail"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.3 }}
            className="border border-t-0 border-line bg-surface p-8 lg:p-10"
          >
            <span className="font-mono text-xs text-signal">{LOOP[active].num} · {LOOP[active].title}</span>
            <p className="mt-3 text-slate-200 leading-relaxed max-w-3xl">{LOOP[active].detail}</p>
          </motion.div>
        </AnimatePresence>
        <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.15em] text-mist">
          Instructor drift and site-fidelity data feed back into the crosswalk — the loop closes on itself.
        </p>
      </section>

      {/* FIVE COMPONENTS TABS */}
      <section className="border-y border-line bg-surface py-24" data-testid="components-tabs-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Reveal>
            <Eyebrow>The five components</Eyebrow>
            <h2 className="mt-5 font-display font-extrabold text-white tracking-tight leading-tight" style={{ fontSize: "clamp(1.75rem, 4vw, 3rem)" }}>
              Purpose, role, deliverables, evidence.
            </h2>
          </Reveal>
          <div className="mt-12 grid lg:grid-cols-[360px_1fr] gap-px bg-line border border-line">
            <div className="bg-ink divide-y divide-line">
              {TABS.map((t, i) => (
                <button
                  key={t.id}
                  data-testid={`component-tab-${t.id}`}
                  onClick={() => setTab(i)}
                  className={`w-full text-left flex items-baseline gap-4 p-6 transition-colors duration-200 ${tab === i ? "bg-surface border-l-2 border-signal" : "hover:bg-surface/60"}`}
                >
                  <span className={`font-mono text-xs ${tab === i ? "text-signal" : "text-mist"}`}>{t.num}</span>
                  <span className={`font-display font-bold tracking-tight ${tab === i ? "text-white" : "text-slate-400"}`}>{t.title}</span>
                </button>
              ))}
            </div>
            <AnimatePresence mode="wait">
              <motion.div
                key={tab}
                data-testid="component-tab-panel"
                initial={{ opacity: 0, x: 16 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -16 }}
                transition={{ duration: 0.3 }}
                className="bg-ink p-8 lg:p-12 grid sm:grid-cols-2 gap-10"
              >
                {[["Purpose", TABS[tab].purpose], ["Institutional role", TABS[tab].role], ["Primary deliverables", TABS[tab].deliverables], ["Evidence of completion", TABS[tab].evidence]].map(([label, text]) => (
                  <div key={label}>
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-signal mb-3">{label}</p>
                    <p className="text-sm text-slate-300 leading-relaxed">{text}</p>
                  </div>
                ))}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </section>

      {/* CROSSWALK TABLE */}
      <section className="py-24 max-w-7xl mx-auto px-6 lg:px-10" data-testid="crosswalk-section">
        <Reveal>
          <Eyebrow>Boundaries</Eyebrow>
          <h2 className="mt-5 font-display font-extrabold text-white tracking-tight leading-tight max-w-3xl" style={{ fontSize: "clamp(1.75rem, 4vw, 3rem)" }}>
            Where T-EMRP operates — and only there.
          </h2>
          <p className="mt-6 text-slate-300 leading-relaxed max-w-2xl">
            The United States already has strong doctrine and a mature national EMS data system. T-EMRP is a connective layer between existing national assets, not a replacement for any of them.
          </p>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="mt-12 overflow-x-auto">
            <table className="tep-table">
              <thead>
                <tr><th>Existing infrastructure</th><th>What it already does</th><th>T-EMRP's non-duplicative contribution</th></tr>
              </thead>
              <tbody>
                {CROSSWALK.map((row, i) => (
                  <tr key={i}>
                    <td className="font-display font-bold text-white whitespace-nowrap">{row[0]}</td>
                    <td>{row[1]}</td>
                    <td>{row[2]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.2em] text-signal">
            No new doctrine. No new registry. No new command authority.
          </p>
        </Reveal>
      </section>

      {/* TECHNOLOGY */}
      <section className="border-t border-line bg-surface py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-16 items-center">
          <Reveal>
            <Eyebrow>Technology</Eyebrow>
            <h2 className="mt-5 font-display font-extrabold text-white tracking-tight leading-tight" style={{ fontSize: "clamp(1.75rem, 4vw, 3rem)" }}>
              A restrained technology pathway.
            </h2>
            <p className="mt-6 text-sm text-slate-300 leading-relaxed">
              Placing artificial intelligence near the center of field triage or predictive clinical decision-making would be premature. During the pilot, software is confined to lower-risk functions: scoring recorded simulation events against transparent published rules, checking documentation completeness, scheduling refresher training, and summarizing de-identified after-action themes for human review.
            </p>
            <ul className="mt-8 space-y-4 text-sm text-slate-400 leading-relaxed">
              <li className="flex gap-3"><span className="text-signal font-mono">→</span> No autonomous triage, clinical recommendation, or real-time treatment control is deployed.</li>
              <li className="flex gap-3"><span className="text-signal font-mono">→</span> Any later clinical decision-support function would require separate validation, usability testing, bias analysis, cybersecurity review, medical oversight, and regulatory assessment before it is proposed to anyone.</li>
            </ul>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/pilot" data-testid="framework-cta-pilot" className="inline-flex items-center gap-3 bg-signal hover:bg-signal-hover text-white font-mono text-xs uppercase tracking-[0.15em] px-6 py-4 transition-colors">
                See the Pilot Model <ArrowUpRight size={15} />
              </Link>
              <Link to="/governance" data-testid="framework-cta-governance" className="inline-flex items-center gap-3 border border-slate-500 hover:border-white text-white font-mono text-xs uppercase tracking-[0.15em] px-6 py-4 transition-colors">
                Governance model <ArrowRight size={15} />
              </Link>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="relative overflow-hidden border border-line">
              <img src={IMAGES.commandCenter} alt="Command center technology" className="w-full h-[420px] object-cover hover:scale-105 transition-transform duration-700" />
              <span className="absolute bottom-0 left-0 bg-ink/80 backdrop-blur-sm font-mono text-[10px] uppercase tracking-[0.2em] text-slate-300 px-4 py-2">
                Human review · Transparent rules
              </span>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
